import { batch, signal } from "@preact/signals";
import { strToU8, zipSync } from "fflate";

import { createLoadedArtifact } from "../archive/LoadedArtifact.js";
import { DecompilerService } from "../decompiler/DecompilerService.js";
import {
  sourceTargetLocationKey,
} from "../decompiler/SourceLocations.js";
import type { DecompiledSource } from "../decompiler/SourcePrinter.js";
import { SearchIndexService } from "../search/SearchIndexService.js";
import { SyntaxHighlightService } from "../syntax/SyntaxHighlightService.js";
import type {
  SearchFilters,
  SearchResponse,
  SearchResult,
} from "../search/SearchIndexProtocol.js";
import {
  formatByteLength,
  inspectFileContent,
} from "../files/FileContent.js";
import type {
  ClassReference,
  CodeLanguage,
  FileReference,
  LoadedArtifact,
  SourceTab,
  SourceTarget,
} from "./types.js";
import {
  normalizeRelativePath,
  sourceTargetRelativePath,
} from "./RelativePath.js";

export interface WorkspaceNotice {
  readonly kind: "error" | "info";
  readonly message: string;
}

export interface ZipProgress {
  readonly current: number;
  readonly total: number;
  readonly classPath: string;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function nextArtifactId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `artifact-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function escapeRegularExpression(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

function sourceSimpleName(internalName: string): string {
  const slash = internalName.lastIndexOf("/");
  const dollar = internalName.lastIndexOf("$");
  return internalName.slice(Math.max(slash, dollar) + 1);
}

function fallbackSourceLine(
  source: string,
  target: SourceTarget,
): number {
  const lines = source.split("\n");
  const name =
    target.kind === "class" || target.kind === "constructor"
      ? sourceSimpleName(target.internalName)
      : target.name;
  const escapedName = escapeRegularExpression(name);
  const namePattern = new RegExp(`\\b${escapedName}\\b`, "u");
  const invocationPattern = new RegExp(`\\b${escapedName}\\s*\\(`, "u");

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!;
    if (!namePattern.test(line)) {
      continue;
    }

    switch (target.kind) {
      case "class":
        if (/\b(?:class|enum|interface|record)\b|@interface/u.test(line)) {
          return index + 1;
        }
        break;
      case "field":
        return index + 1;
      case "constructor":
        if (
          invocationPattern.test(line) &&
          !new RegExp(`\\bnew\\s+${escapedName}\\b`, "u").test(line)
        ) {
          return index + 1;
        }
        break;
      case "method":
        if (invocationPattern.test(line)) {
          return index + 1;
        }
        break;
    }
  }

  if (target.kind === "constructor") {
    return fallbackSourceLine(source, {
      kind: "class",
      internalName: target.internalName,
    });
  }

  return 1;
}

function revealLine(
  locations: Readonly<Record<string, number>>,
  source: string,
  target: SourceTarget,
): number {
  return (
    locations[sourceTargetLocationKey(target)] ??
    fallbackSourceLine(source, target)
  );
}

function activeAfterRemoval(
  tabs: readonly SourceTab[],
  activeTabId: string | null,
  keep: (tab: SourceTab) => boolean,
): string | null {
  if (activeTabId === null) {
    return null;
  }

  const activeIndex = tabs.findIndex((tab) => tab.id === activeTabId);
  const activeTab = tabs[activeIndex];
  if (activeTab !== undefined && keep(activeTab)) {
    return activeTabId;
  }

  for (let index = activeIndex + 1; index < tabs.length; index++) {
    const tab = tabs[index]!;
    if (keep(tab)) {
      return tab.id;
    }
  }
  for (let index = activeIndex - 1; index >= 0; index--) {
    const tab = tabs[index]!;
    if (keep(tab)) {
      return tab.id;
    }
  }

  return null;
}

function safePathSegment(value: string): string {
  return value
    .replace(/[<>:"|?*\u0000-\u001f]/gu, "_")
    .replace(/[. ]+$/gu, "") || "file";
}

function safeZipPath(value: string): string {
  const segments = value
    .replaceAll("\\", "/")
    .split("/")
    .filter((segment) => segment.length > 0 && segment !== "." && segment !== "..")
    .map(safePathSegment);
  return segments.join("/") || "source.java";
}

function artifactFolderName(name: string): string {
  const leaf = name.replaceAll("\\", "/").split("/").at(-1) ?? name;
  const dot = leaf.lastIndexOf(".");
  return safePathSegment(dot > 0 ? leaf.slice(0, dot) : leaf);
}

function sourceEntryPath(reference: ClassReference): string {
  let path = reference.entryPath.replaceAll("\\", "/").replace(/^\/+/, "");
  if (path.startsWith("classes/")) {
    path = path.slice("classes/".length);
  }
  if (!path.includes("/") && reference.internalName.includes("/")) {
    path = `${reference.internalName}.class`;
  }
  path = /\.class$/iu.test(path)
    ? path.replace(/\.class$/iu, ".java")
    : `${path}.java`;
  return safeZipPath(path);
}

function uniquePath(
  requested: string,
  existing: Readonly<Record<string, Uint8Array>>,
): string {
  if (existing[requested] === undefined) {
    return requested;
  }

  const dot = requested.lastIndexOf(".");
  const base = dot === -1 ? requested : requested.slice(0, dot);
  const extension = dot === -1 ? "" : requested.slice(dot);
  let index = 2;
  while (existing[`${base}-${index}${extension}`] !== undefined) {
    index++;
  }
  return `${base}-${index}${extension}`;
}

function downloadBytes(
  bytes: Uint8Array,
  fileName: string,
  mediaType = "application/octet-stream",
): void {
  const data = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  const url = URL.createObjectURL(new Blob([data], { type: mediaType }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function writeClipboardText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText !== undefined) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall through to the legacy copy path for restricted browser contexts.
    }
  }

  const input = document.createElement("textarea");
  input.value = value;
  input.readOnly = true;
  input.style.position = "fixed";
  input.style.left = "-10000px";
  input.style.top = "0";
  document.body.append(input);
  input.select();
  const copied = document.execCommand("copy");
  input.remove();
  if (!copied) {
    throw new Error("The browser did not permit clipboard access.");
  }
}

export class WorkspaceStore {
  public readonly artifacts = signal<readonly LoadedArtifact[]>([]);
  public readonly tabs = signal<readonly SourceTab[]>([]);
  public readonly activeTabId = signal<string | null>(null);
  public readonly loadingFiles = signal(false);
  public readonly savingZip = signal(false);
  public readonly zipProgress = signal<ZipProgress | null>(null);
  public readonly notice = signal<WorkspaceNotice | null>(null);

  private readonly decompiler = new DecompilerService(
    new URL("jmods/", document.baseURI),
  );
  private readonly searchIndex = new SearchIndexService();
  private readonly syntaxHighlighter = new SyntaxHighlightService();
  public readonly searchIndexRevision = this.searchIndex.revision;
  public readonly searchIndexingArtifactCount =
    this.searchIndex.indexingArtifactCount;
  private readonly decompiledSources = new Map<string, DecompiledSource>();
  private readonly decompilationJobs = new Map<
    string,
    Promise<DecompiledSource>
  >();
  private revealRequestId = 0;

  public async addFiles(files: readonly File[]): Promise<void> {
    if (files.length === 0) {
      return;
    }

    batch(() => {
      this.loadingFiles.value = true;
      this.notice.value = null;
    });
    const failures: string[] = [];

    try {
      for (const file of files) {
        const artifactId = nextArtifactId();
        this.searchIndex.registerArtifact(artifactId, file);
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const artifact = createLoadedArtifact(
            artifactId,
            file.name,
            bytes,
          );
          await this.decompiler.registerArtifact(artifactId, file);
          this.artifacts.value = [...this.artifacts.value, artifact];
        } catch (error) {
          this.searchIndex.unregisterArtifact(artifactId);
          failures.push(`${file.name}: ${errorMessage(error)}`);
        }
      }
    } finally {
      this.loadingFiles.value = false;
    }

    if (failures.length > 0) {
      this.notice.value = {
        kind: "error",
        message: failures.join("\n"),
      };
    }
  }

  public openClass(
    reference: ClassReference,
    target: SourceTarget = {
      kind: "class",
      internalName: reference.internalName,
    },
    relativePath: string = sourceTargetRelativePath(reference, target),
  ): void {
    const requestId = ++this.revealRequestId;
    const existing = this.tabs.value.find((tab) => tab.id === reference.id);
    if (existing !== undefined) {
      const line =
        existing.status === "ready"
          ? revealLine(existing.locations, existing.text, target)
          : null;
      batch(() => {
        this.tabs.value = this.tabs.value.map((tab) =>
          tab.id === reference.id
            ? {
                ...tab,
                revealTarget: target,
                revealLine: line,
                revealRequestId: requestId,
                relativePath,
              }
            : tab,
        );
        this.activeTabId.value = existing.id;
      });
      return;
    }

    const tab: SourceTab = {
      id: reference.id,
      artifactId: reference.artifactId,
      classReference: reference,
      title: `${reference.displayName}.java`,
      subtitle: reference.entryPath,
      relativePath,
      status: "loading",
      statusText: "Queued for decompilation…",
      contentKind: "text",
      language: "java",
      text: "",
      highlightedHtml: null,
      bytes: null,
      error: null,
      locations: {},
      folds: [],
      revealTarget: target,
      revealLine: null,
      revealRequestId: requestId,
    };

    batch(() => {
      this.tabs.value = [...this.tabs.value, tab];
      this.activeTabId.value = tab.id;
    });

    void this.getDecompiledSource(reference, ({ message }) => {
      this.updateTab(reference.id, {
        statusText: message,
      });
    })
      .then(async (result) => {
        const current = this.tabs.value.find((candidate) => candidate.id === reference.id);
        if (current === undefined) {
          return;
        }
        this.updateTab(reference.id, {
          statusText: "Applying syntax highlighting…",
          text: result.source,
          highlightedHtml: null,
          locations: result.locations,
          folds: result.folds,
          error: null,
          revealLine:
            current.revealTarget === null
              ? null
              : revealLine(
                  result.locations,
                  result.source,
                  current.revealTarget,
                ),
        });
        await this.finishSyntaxHighlighting(
          reference.id,
          result.source,
          "java",
          "Decompiled",
        );
      })
      .catch((error: unknown) => {
        this.updateTab(reference.id, {
          status: "error",
          statusText: "Decompilation failed",
          error: errorMessage(error),
        });
      });
  }

  public openFile(reference: FileReference): void {
    const existing = this.tabs.value.find((tab) => tab.id === reference.id);
    if (existing !== undefined) {
      this.activeTabId.value = existing.id;
      return;
    }

    const tab: SourceTab = {
      id: reference.id,
      artifactId: reference.artifactId,
      classReference: null,
      title: reference.displayName,
      subtitle: reference.entryPath,
      relativePath: normalizeRelativePath(reference.entryPath),
      status: "loading",
      statusText: "Reading file…",
      contentKind: "text",
      language: "text",
      text: "",
      highlightedHtml: null,
      bytes: null,
      error: null,
      locations: {},
      folds: [],
      revealTarget: null,
      revealLine: null,
      revealRequestId: 0,
    };

    batch(() => {
      this.tabs.value = [...this.tabs.value, tab];
      this.activeTabId.value = tab.id;
    });

    void Promise.resolve()
      .then(() => reference.readBytes())
      .then(async (bytes) => {
        const content = inspectFileContent(reference.entryPath, bytes);
        if (content.kind === "text") {
          const readyStatusText = `Text · ${formatByteLength(bytes.length)}`;
          this.updateTab(reference.id, {
            statusText: "Applying syntax highlighting…",
            contentKind: "text",
            language: content.language,
            text: content.text,
            highlightedHtml: null,
            bytes: null,
            error: null,
          });
          await this.finishSyntaxHighlighting(
            reference.id,
            content.text,
            content.language,
            readyStatusText,
          );
        } else {
          this.updateTab(reference.id, {
            status: "ready",
            statusText: `Binary · ${formatByteLength(bytes.length)}`,
            contentKind: "binary",
            text: "",
            highlightedHtml: null,
            bytes: content.bytes,
            error: null,
          });
        }
      })
      .catch((error: unknown) => {
        this.updateTab(reference.id, {
          status: "error",
          statusText: "Unable to read file",
          error: errorMessage(error),
        });
      });
  }

  public searchSymbols(
    query: string,
    filters: SearchFilters,
  ): Promise<SearchResponse> {
    return this.searchIndex.search(query, filters);
  }

  public openSearchResult(result: SearchResult): void {
    const artifact = this.artifacts.value.find(
      (candidate) => candidate.id === result.artifactId,
    );
    const resolution = artifact?.resolveClassEntry(result.filePath) ?? null;
    if (resolution !== null) {
      this.openClass(resolution.reference, resolution.target);
    }
  }

  public activateTab(tabId: string): void {
    if (this.tabs.value.some((tab) => tab.id === tabId)) {
      this.activeTabId.value = tabId;
    }
  }

  public closeTab(tabId: string): void {
    const tabs = this.tabs.value;
    const remaining = tabs.filter((tab) => tab.id !== tabId);
    if (remaining.length === tabs.length) {
      return;
    }

    const nextActive = activeAfterRemoval(
      tabs,
      this.activeTabId.value,
      (tab) => tab.id !== tabId,
    );

    batch(() => {
      this.tabs.value = remaining;
      this.activeTabId.value = nextActive;
    });
  }

  public closeAllTabs(): void {
    if (this.tabs.value.length === 0) {
      return;
    }
    batch(() => {
      this.tabs.value = [];
      this.activeTabId.value = null;
    });
  }

  public closeOtherTabs(tabId: string): void {
    const tab = this.tabs.value.find((candidate) => candidate.id === tabId);
    if (tab === undefined || this.tabs.value.length <= 1) {
      return;
    }
    batch(() => {
      this.tabs.value = [tab];
      this.activeTabId.value = tab.id;
    });
  }

  public async copyRelativePath(relativePath: string): Promise<void> {
    const path = normalizeRelativePath(relativePath);
    try {
      await writeClipboardText(path);
      this.notice.value = {
        kind: "info",
        message: `Copied relative path: ${path}`,
      };
    } catch (error) {
      this.notice.value = {
        kind: "error",
        message: `Unable to copy relative path: ${errorMessage(error)}`,
      };
    }
  }

  public async exportDecompiledSource(
    reference: ClassReference,
  ): Promise<void> {
    try {
      const result = await this.getDecompiledSource(reference);
      const sourcePath = sourceEntryPath(reference);
      const fileName = sourcePath.split("/").at(-1) ?? "source.java";
      downloadBytes(
        strToU8(result.source),
        fileName,
        "text/x-java-source;charset=utf-8",
      );
      this.notice.value = {
        kind: "info",
        message: `Exported decompiled source: ${fileName}`,
      };
    } catch (error) {
      this.notice.value = {
        kind: "error",
        message: `Unable to export decompiled source: ${errorMessage(error)}`,
      };
    }
  }

  public removeArtifact(artifactId: string): void {
    const artifact = this.artifacts.value.find((candidate) => candidate.id === artifactId);
    if (artifact === undefined) {
      return;
    }

    const tabs = this.tabs.value;
    const keepTab = (tab: SourceTab): boolean => tab.artifactId !== artifactId;
    const nextActive = activeAfterRemoval(tabs, this.activeTabId.value, keepTab);

    for (const reference of artifact.classes) {
      this.decompiledSources.delete(reference.id);
      this.decompilationJobs.delete(reference.id);
    }
    void this.decompiler.unregisterArtifact(artifactId).catch(() => undefined);
    this.searchIndex.unregisterArtifact(artifactId);

    batch(() => {
      this.artifacts.value = this.artifacts.value.filter(
        (candidate) => candidate.id !== artifactId,
      );
      this.tabs.value = tabs.filter(keepTab);
      this.activeTabId.value = nextActive;
    });
  }

  public clearWorkspace(): void {
    this.decompiledSources.clear();
    this.decompilationJobs.clear();
    void this.decompiler.clearArtifacts().catch(() => undefined);
    this.searchIndex.clear();
    batch(() => {
      this.artifacts.value = [];
      this.tabs.value = [];
      this.activeTabId.value = null;
      this.notice.value = null;
    });
  }

  public async saveDecompiledZip(): Promise<void> {
    if (this.savingZip.value) {
      return;
    }

    const artifacts = this.artifacts.value;
    const work = artifacts.flatMap((artifact) =>
      artifact.classes.map((reference) => ({ artifact, reference })),
    );
    if (work.length === 0) {
      this.notice.value = {
        kind: "info",
        message: "No Java classes are loaded.",
      };
      return;
    }

    batch(() => {
      this.savingZip.value = true;
      this.zipProgress.value = null;
      this.notice.value = null;
    });
    const entries: Record<string, Uint8Array> = {};
    const failures: string[] = [];
    const usedFolders = new Map<string, number>();
    const folders = new Map<string, string>();

    for (const artifact of artifacts) {
      const base = artifactFolderName(artifact.name);
      const count = (usedFolders.get(base) ?? 0) + 1;
      usedFolders.set(base, count);
      folders.set(artifact.id, count === 1 ? base : `${base}-${count}`);
    }

    try {
      for (let index = 0; index < work.length; index++) {
        const { artifact, reference } = work[index]!;
        this.zipProgress.value = {
          current: index + 1,
          total: work.length,
          classPath: reference.entryPath,
        };

        try {
          const result = await this.getDecompiledSource(reference);
          const folder = folders.get(artifact.id) ?? "artifact";
          const requestedPath = `${folder}/${sourceEntryPath(reference)}`;
          entries[uniquePath(requestedPath, entries)] = strToU8(result.source);
        } catch (error) {
          failures.push(`${artifact.name}/${reference.entryPath}: ${errorMessage(error)}`);
        }
      }

      if (failures.length > 0) {
        entries["DECOMPILATION_ERRORS.txt"] = strToU8(
          `${failures.join("\n")}\n`,
        );
      }

      const archiveBytes = zipSync(entries, { level: 6 });
      const fileName =
        artifacts.length === 1
          ? `${artifactFolderName(artifacts[0]!.name)}-decompiled.zip`
          : "decompiled-sources.zip";
      downloadBytes(archiveBytes, fileName, "application/zip");

      const sourceCount = work.length - failures.length;
      this.notice.value = {
        kind: failures.length === 0 ? "info" : "error",
        message:
          failures.length === 0
            ? `Saved ${sourceCount.toLocaleString()} decompiled source${sourceCount === 1 ? "" : "s"}.`
            : `Saved ${sourceCount.toLocaleString()} decompiled sources; ${failures.length.toLocaleString()} failed. See DECOMPILATION_ERRORS.txt in the zip.`,
      };
    } catch (error) {
      this.notice.value = {
        kind: "error",
        message: `Unable to save decompiled zip: ${errorMessage(error)}`,
      };
    } finally {
      batch(() => {
        this.savingZip.value = false;
        this.zipProgress.value = null;
      });
    }
  }

  public dismissNotice(): void {
    this.notice.value = null;
  }

  private getDecompiledSource(
    reference: ClassReference,
    onProgress?: (progress: { readonly message: string }) => void,
  ): Promise<DecompiledSource> {
    const cached = this.decompiledSources.get(reference.id);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }

    const existing = this.decompilationJobs.get(reference.id);
    if (existing !== undefined) {
      return existing;
    }

    const job = this.decompiler.decompile(reference, onProgress).then((result) => {
      if (
        this.artifacts.value.some(
          (artifact) => artifact.id === reference.artifactId,
        )
      ) {
        this.decompiledSources.set(reference.id, result);
      }
      return result;
    });
    this.decompilationJobs.set(reference.id, job);
    void job.then(
      () => {
        if (this.decompilationJobs.get(reference.id) === job) {
          this.decompilationJobs.delete(reference.id);
        }
      },
      () => {
        if (this.decompilationJobs.get(reference.id) === job) {
          this.decompilationJobs.delete(reference.id);
        }
      },
    );
    return job;
  }

  private async finishSyntaxHighlighting(
    tabId: string,
    source: string,
    language: CodeLanguage,
    readyStatusText: string,
  ): Promise<void> {
    try {
      const highlightedHtml = await this.syntaxHighlighter.highlight(
        source,
        language,
      );
      this.updateTab(tabId, {
        status: "ready",
        statusText: readyStatusText,
        highlightedHtml,
        error: null,
      });
    } catch (error) {
      console.error(`Unable to syntax-highlight ${tabId}`, error);
      this.updateTab(tabId, {
        status: "ready",
        statusText: `${readyStatusText} · plain text`,
        highlightedHtml: null,
        error: null,
      });
    }
  }

  private updateTab(
    tabId: string,
    patch: Partial<
      Omit<
        SourceTab,
        | "id"
        | "artifactId"
        | "classReference"
        | "title"
        | "subtitle"
      >
    >,
  ): void {
    this.tabs.value = this.tabs.value.map((tab) =>
      tab.id === tabId ? { ...tab, ...patch } : tab,
    );
  }
}

export const workspace = new WorkspaceStore();
